import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Building2, 
  User, 
  Mail, 
  Phone, 
  Instagram, 
  Briefcase,
  Clock,
  MapPin,
  Calendar,
  FileText,
  Link,
  Gift,
  CheckCircle,
  Loader2,
  Sparkles,
  ArrowLeft
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BoothApplicationPage = () => {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [promoValid, setPromoValid] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);

  const [form, setForm] = useState({
    applicant_name: "",
    applicant_email: "",
    applicant_phone: "",
    applicant_instagram: "",
    specialty: "",
    years_experience: 0,
    current_location: "",
    desired_booth: "",
    availability_date: "",
    why_interested: "",
    portfolio_url: "",
    promo_code: ""
  });

  useEffect(() => {
    fetchOwnerProfile();
  }, [ownerId]);

  const fetchOwnerProfile = async () => {
    try {
      const response = await axios.get(`${API}/booth/owner-profile/${ownerId}`);
      setOwnerProfile(response.data);
    } catch (error) {
      console.error("Failed to fetch owner profile:", error);
      toast.error("Salon not found");
    } finally {
      setLoading(false);
    }
  };

  const validatePromoCode = async () => {
    if (!form.promo_code.trim()) {
      setPromoValid(null);
      return;
    }

    setPromoChecking(true);
    try {
      const response = await axios.get(
        `${API}/booth/promo-codes/${form.promo_code}/validate?owner_id=${ownerId}`
      );
      setPromoValid(response.data);
      if (response.data.valid) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      setPromoValid({ valid: false, message: "Error validating code" });
    } finally {
      setPromoChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.applicant_name || !form.applicant_email) {
      toast.error("Please fill in required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/booth/applications`, {
        ...form,
        owner_id: ownerId,
        years_experience: parseInt(form.years_experience) || 0
      });
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!ownerProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Salon Not Found</h2>
            <p className="text-gray-500 mb-4">This booth rental listing is no longer available.</p>
            <Button onClick={() => navigate('/')}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Application Submitted!</h2>
            <p className="text-gray-500 mb-6">
              Thank you for your interest in renting a booth at {ownerProfile.salon_name}. 
              The salon owner will review your application and contact you soon.
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/')} className="w-full">
                Back to Home
              </Button>
              <Button variant="outline" onClick={() => {
                setSubmitted(false);
                setForm({
                  applicant_name: "",
                  applicant_email: "",
                  applicant_phone: "",
                  applicant_instagram: "",
                  specialty: "",
                  years_experience: 0,
                  current_location: "",
                  desired_booth: "",
                  availability_date: "",
                  why_interested: "",
                  portfolio_url: "",
                  promo_code: ""
                });
              }} className="w-full">
                Submit Another Application
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Booth Rental Application
          </h1>
          <p className="text-purple-300 text-lg">
            {ownerProfile.salon_name}
          </p>
          {ownerProfile.location?.city && (
            <p className="text-gray-400 flex items-center justify-center gap-1 mt-1">
              <MapPin className="w-4 h-4" />
              {ownerProfile.location.city}, {ownerProfile.location.state}
            </p>
          )}
        </div>

        {/* Application Form */}
        <Card className="bg-white/95 backdrop-blur">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              Your Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    Full Name *
                  </Label>
                  <Input
                    value={form.applicant_name}
                    onChange={(e) => setForm({...form, applicant_name: e.target.value})}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    Email *
                  </Label>
                  <Input
                    type="email"
                    value={form.applicant_email}
                    onChange={(e) => setForm({...form, applicant_email: e.target.value})}
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    Phone Number
                  </Label>
                  <Input
                    type="tel"
                    value={form.applicant_phone}
                    onChange={(e) => setForm({...form, applicant_phone: e.target.value})}
                    placeholder="+1 555 123 4567"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Instagram className="w-4 h-4" />
                    Instagram Handle
                  </Label>
                  <Input
                    value={form.applicant_instagram}
                    onChange={(e) => setForm({...form, applicant_instagram: e.target.value})}
                    placeholder="@yourusername"
                  />
                </div>
              </div>

              {/* Professional Info */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-purple-500" />
                  Professional Background
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Specialty</Label>
                    <Select
                      value={form.specialty}
                      onValueChange={(v) => setForm({...form, specialty: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select your specialty" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="barber">Barber</SelectItem>
                        <SelectItem value="hairstylist">Hair Stylist</SelectItem>
                        <SelectItem value="colorist">Colorist</SelectItem>
                        <SelectItem value="braider">Braider</SelectItem>
                        <SelectItem value="loctician">Loctician</SelectItem>
                        <SelectItem value="nail_tech">Nail Technician</SelectItem>
                        <SelectItem value="esthetician">Esthetician</SelectItem>
                        <SelectItem value="makeup_artist">Makeup Artist</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Years of Experience
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.years_experience}
                      onChange={(e) => setForm({...form, years_experience: e.target.value})}
                      placeholder="5"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Current Location / Salon</Label>
                  <Input
                    value={form.current_location}
                    onChange={(e) => setForm({...form, current_location: e.target.value})}
                    placeholder="Currently at ABC Salon, Downtown"
                  />
                </div>

                <div className="mt-4">
                  <Label className="flex items-center gap-1">
                    <Link className="w-4 h-4" />
                    Portfolio URL (optional)
                  </Label>
                  <Input
                    type="url"
                    value={form.portfolio_url}
                    onChange={(e) => setForm({...form, portfolio_url: e.target.value})}
                    placeholder="https://instagram.com/yourwork"
                  />
                </div>
              </div>

              {/* Booth Preferences */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-500" />
                  Booth Preferences
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Preferred Booth/Suite (if any)</Label>
                    <Input
                      value={form.desired_booth}
                      onChange={(e) => setForm({...form, desired_booth: e.target.value})}
                      placeholder="e.g., Corner booth, Suite 3"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Available Start Date
                    </Label>
                    <Input
                      type="date"
                      value={form.availability_date}
                      onChange={(e) => setForm({...form, availability_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Why are you interested in this salon?</Label>
                  <Textarea
                    value={form.why_interested}
                    onChange={(e) => setForm({...form, why_interested: e.target.value})}
                    placeholder="Tell us a bit about yourself and why you'd be a great fit..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Promo Code */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-purple-500" />
                  Promo Code (optional)
                </h3>
                
                <div className="flex gap-2">
                  <Input
                    value={form.promo_code}
                    onChange={(e) => {
                      setForm({...form, promo_code: e.target.value.toUpperCase()});
                      setPromoValid(null);
                    }}
                    placeholder="FREEMONTH"
                    className="uppercase"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={validatePromoCode}
                    disabled={promoChecking || !form.promo_code}
                  >
                    {promoChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
                
                {promoValid && (
                  <div className={`mt-2 p-3 rounded-lg ${promoValid.valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {promoValid.valid ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        <span>{promoValid.message}</span>
                        {promoValid.promo_type === 'free_months' && (
                          <Badge className="bg-green-100 text-green-700">
                            {promoValid.value} month(s) free!
                          </Badge>
                        )}
                        {promoValid.promo_type === 'percent_off' && (
                          <Badge className="bg-green-100 text-green-700">
                            {promoValid.value}% off!
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span>{promoValid.message}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-gray-500">
                By submitting, you agree to be contacted regarding this booth rental opportunity.
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm">
            Powered by <span className="text-purple-400 font-medium">MyHairChair</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BoothApplicationPage;
